import { useCallback } from 'react'
import { Web3Provider } from '@ethersproject/providers'
import type { ExternalProvider } from '@ethersproject/providers'
import { SessionType } from '@mprolab/sdk'
import { ProgressState } from 'mobxStores/IndexNodeStore'
import { useLazyGetSignatureHashQuery, useLoginMutation } from 'state/services/indexNode/indexNode'
import type { SignableWallet } from 'utils/getSignature'

interface VerifyIndexNodeArgs {
  account?: string
  provider?: SignableWallet
  sessionType?: SessionType
  successCallback?: (accessToken: string) => void | Promise<void>
  errorCallback?: (error: unknown) => void | Promise<void>
  setProgress?: (progress: ProgressState) => void
}

const signIndexNodeMessage = async (account: string, provider: SignableWallet, hash: string) => {
  const walletAddress = account.toLowerCase()
  const verifyMessage = `Please sign to let us verify\nthat you are the owner of this address\n${walletAddress}\n\nRequest ID ${hash}`

  const web3 =
    provider instanceof Web3Provider
      ? provider
      : new Web3Provider((await provider.getProvider()) as ExternalProvider, 'any')

  const signer = web3.getSigner(account)
  return signer.signMessage(verifyMessage)
}

export const useVerifyIndexNode = () => {
  const [getSignatureHash] = useLazyGetSignatureHashQuery()
  const [login] = useLoginMutation()

  return useCallback(
    async ({
      account,
      provider,
      sessionType = SessionType.LIFETIME,
      successCallback,
      errorCallback,
      setProgress
    }: VerifyIndexNodeArgs) => {
      if (!account || !provider) {
        const error = new Error('Wallet account or provider missing')
        setProgress?.(ProgressState.ERROR)
        if (errorCallback) await errorCallback(error)
        return error
      }

      try {
        const walletAddress = account.toLowerCase()

        setProgress?.(ProgressState.PENDING)
        const { hash } = await getSignatureHash({ account: walletAddress }).unwrap()

        setProgress?.(ProgressState.CONFIRMING)
        const signature = await signIndexNodeMessage(account, provider, hash)

        const response = await login({
          wallet: walletAddress,
          signature,
          account: walletAddress,
          hash,
          sessionType
        }).unwrap()

        const accessToken = response?.access_token?.trim()
        if (!accessToken) {
          throw new Error('Index node access token missing')
        }

        if (successCallback) {
          await successCallback(accessToken)
        }
        setProgress?.(ProgressState.SUCCESS)

        return accessToken
      } catch (error) {
        setProgress?.(ProgressState.ERROR)
        if (errorCallback) await errorCallback(error)
        return error
      }
    },
    [getSignatureHash, login]
  )
}

export default useVerifyIndexNode
